import { useState, useRef } from 'react';
import {
	DocumentIcon,
	XMarkIcon,
	FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/20/solid';

import { StackableModal } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';

import useModal from '../hooks/useModal';

const MODAL_NAME = 'serviceReportUpload';

const ServiceReportUploadModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openDocumentPicker } = useModal('documentPicker');
	const fileInputRef = useRef(null);

	const [file, setFile] = useState(null);
	const [selectedExistingDoc, setSelectedExistingDoc] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [showUploadOptions, setShowUploadOptions] = useState(false);

	// Filter existing documents to only show PDFs
	const existingPDFs = (open?.existingDocuments || []).filter(
		(doc) =>
			doc.fileType === 'application/pdf' ||
			doc.name?.toLowerCase().endsWith('.pdf'),
	);

	const handleClose = () => {
		setFile(null);
		setSelectedExistingDoc(null);
		setClose();
		// Call onCancel callback if provided
		if (typeof open?.onCancel === 'function') {
			open.onCancel();
		}
	};

	const handleFileChange = (e) => {
		const selectedFile = e.target.files?.[0];
		if (selectedFile && selectedFile.type === 'application/pdf') {
			setFile(selectedFile);
		} else if (selectedFile) {
			alert('Please select a PDF file');
		}
	};

	const handleSubmit = async () => {
		if (!file && !selectedExistingDoc) return;

		setIsUploading(true);

		try {
			if (selectedExistingDoc) {
				// Use existing document
				if (typeof open?.onSelectExisting === 'function') {
					await open.onSelectExisting(selectedExistingDoc);
				}
			} else if (file) {
				// Upload new file
				if (typeof open?.onUpload === 'function') {
					await open.onUpload(file);
				}
			}
			setFile(null);
			setSelectedExistingDoc(null);
			setClose();
		} catch (error) {
			console.error('Failed to process service report:', error);
		} finally {
			setIsUploading(false);
		}
	};

	const handleSelectFromLibrary = () => {
		setShowUploadOptions(false);
		openDocumentPicker({
			multiSelect: false,
			fileTypes: ['application/pdf'],
			onSelect: async (selectedDocs) => {
				if (selectedDocs?.length > 0) {
					const selectedDoc = selectedDocs[0];
					// Create a file-like object from the selected document
					const docFile = {
						name: selectedDoc.name || selectedDoc.fileName || 'document.pdf',
						size: selectedDoc.size || 0,
						type: 'application/pdf',
						id: selectedDoc.id,
						fromLibrary: true,
					};
					setFile(docFile);
				}
			},
		});
	};

	const removeFile = () => {
		setFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			size="md"
			title="Upload Service Report"
		>
			<div className="">
				<p className="text-sm text-gray-600 mb-4">
					A service report is required to complete this work order. Please
					{existingPDFs.length > 0
						? ' select an existing attachment, upload a PDF document, or choose one from your library.'
						: ' upload a PDF document or choose one from your library.'}
				</p>

				{/* Existing Attachments Section */}
				{existingPDFs.length > 0 && !file && !selectedExistingDoc && (
					<div className="mt-4">
						<p className="text-xs font-medium text-gray-700 mb-4">
							Select from existing attachments:
						</p>
						<div className="space-y-2 mt-2">
							{existingPDFs.map((doc) => (
								<button
									key={doc.id}
									type="button"
									onClick={() => setSelectedExistingDoc(doc)}
									className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-indigo-300 transition-colors text-left"
								>
									<div className="flex items-center gap-3">
										<DocumentIcon className="h-6 w-6 text-red-500" />
										<div>
											<p className="text-sm font-medium text-gray-900">
												{doc.name || doc.fileName || 'Document'}
											</p>
											<p className="text-xs text-gray-500">
												{doc.size
													? `${(doc.size / 1024 / 1024).toFixed(2)} MB`
													: 'PDF Document'}
											</p>
										</div>
									</div>
									<div className="text-xs text-indigo-600 font-medium">
										Select
									</div>
								</button>
							))}
						</div>
						<div className="mt-4 mb-2 flex items-center gap-2">
							<div className="flex-1 border-t border-gray-300"></div>
							<span className="text-xs text-gray-500">OR</span>
							<div className="flex-1 border-t border-gray-300"></div>
						</div>
					</div>
				)}

				{/* Upload Options Button */}
				{!file && !selectedExistingDoc && (
					<div className="relative mt-6">
						<button
							type="button"
							onClick={() => setShowUploadOptions(!showUploadOptions)}
							className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
						>
							<PlusIcon className="w-5 h-5" />
							{isUploading ? 'Uploading...' : 'Add Service Report'}
						</button>

						{/* Upload Options Dropdown */}
						{showUploadOptions && (
							<>
								<div
									className="fixed inset-0 z-40"
									onClick={() => setShowUploadOptions(false)}
								/>
								<div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
									<button
										type="button"
										onClick={() => {
											fileInputRef.current?.click();
											setShowUploadOptions(false);
										}}
										className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
									>
										<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
										Upload File
									</button>
									<button
										type="button"
										onClick={handleSelectFromLibrary}
										className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
									>
										<FolderOpenIcon className="w-4 h-4 text-gray-500" />
										Choose from Library
									</button>
								</div>
							</>
						)}

						{/* Hidden file input */}
						<input
							ref={fileInputRef}
							type="file"
							accept="application/pdf"
							onChange={handleFileChange}
							className="hidden"
						/>
					</div>
				)}

				{/* Selected file display */}
				{file && (
					<div className="mt-6">
						<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
							<div className="flex items-center gap-3">
								<DocumentIcon className="h-8 w-8 text-red-500" />
								<div className="text-left">
									<p className="text-sm font-medium text-gray-900">
										{file.name}
									</p>
									<p className="text-xs text-gray-500">
										{file.fromLibrary ? (
											'From Library'
										) : (
											<>{(file.size / 1024 / 1024).toFixed(2)} MB</>
										)}
									</p>
								</div>
							</div>
							<button
								type="button"
								onClick={removeFile}
								className="p-1 text-gray-400 hover:text-red-500"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>
					</div>
				)}

				{/* Selected existing document display */}
				{selectedExistingDoc && (
					<div className="mt-6">
						<div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
							<div className="flex items-center gap-3">
								<DocumentIcon className="h-8 w-8 text-red-500" />
								<div className="text-left">
									<p className="text-sm font-medium text-gray-900">
										{selectedExistingDoc.name ||
											selectedExistingDoc.fileName ||
											'Document'}
									</p>
									<p className="text-xs text-indigo-600 font-medium">
										Existing Work Order Attachment
									</p>
								</div>
							</div>
							<button
								type="button"
								onClick={() => setSelectedExistingDoc(null)}
								className="p-1 text-gray-400 hover:text-red-500"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>
					</div>
				)}

				{/* Actions */}
				<div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={(!file && !selectedExistingDoc) || isUploading}
						className={classNames(
							'px-4 py-2 text-sm font-medium text-white rounded-md',
							(!file && !selectedExistingDoc) || isUploading
								? 'bg-gray-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700',
						)}
					>
						{isUploading
							? 'Processing...'
							: selectedExistingDoc
								? 'Use Selected & Complete'
								: 'Upload & Complete'}
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default ServiceReportUploadModal;
